import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateOnPremSubtitleAssetLanguageResponse } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremSubtitleAssetLanguageResponse";
import { ValidateOnPremSubtitleAssetLanguageRequest } from "../../../../../../common/api/master-api/assets/details/ValidateOnPremSubtitleAssetLanguageRequest";
import { useAssetsApi } from "../../../../../../common/api/master-api/assets/useAssetsApi";
import { OnPremAssetQueryKey } from "../../../common/OnPremAssetQueryKey";

export interface UseEditOnPremAsset {
    validateOnPremSubtitleAssetLanguage: SprintMutationResult<
        ValidateOnPremSubtitleAssetLanguageRequest,
        ValidationResult
    >;
    updateOnPremSubtitleAssetLanguage: SprintMutationResult<
        ValidateOnPremSubtitleAssetLanguageRequest,
        UpdateOnPremSubtitleAssetLanguageResponse
    >;
}

export const useEditOnPremSubtitleAssetLanguage = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditOnPremAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateOnPremSubtitleAssetLanguage = useSprintMutation<
        ValidateOnPremSubtitleAssetLanguageRequest,
        ValidationResult
    >({
        mutationFn: async ({ assetId, subtitleLanguageId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate subtitle language.");
            }

            return assetsApi.validateOnPremSubtitleAssetLanguage({ assetId, subtitleLanguageId });
        }
    });

    const updateOnPremSubtitleAssetLanguage = useSprintMutation<
        ValidateOnPremSubtitleAssetLanguageRequest,
        UpdateOnPremSubtitleAssetLanguageResponse
    >({
        mutationFn: async ({ assetId, subtitleLanguageId }: ValidateOnPremSubtitleAssetLanguageRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update subtitle language.");
            }

            return assetsApi.updateOnPremSubtitleAssetLanguage({ assetId, subtitleLanguageId });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [OnPremAssetQueryKey.OnPremAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateOnPremSubtitleAssetLanguage,
        updateOnPremSubtitleAssetLanguage
    };
};
